import { ChangeEvent } from "react";

import { Checkbox, Collapse, RotatingChevron } from "@newsela/angelou";
import { withTrackEvent } from "@newsela/snitch";

import { useSearchAdminPermissions } from "@/search/NewSearchPageMVP/provider/hooks";
import { TopicsDataQASelector } from "../../Topics.constants";
import { RecursiveTags } from "../RecursiveTags";
import { TaxonomyLevelEnum } from "../TaxonomyTags.constants";
import { useTaxonomyTagsContext } from "../TaxonomyTags.provider";
import { ITaxonomyContent } from "../TaxonomyTags.types";
import {
  collapseButton,
  collapseLabel,
  collapseStyle,
  collapseTitleContainer,
} from "./CollapsibleTags.styles";

interface CollapsibleTagsProps {
  categorizedData: { [key: string]: ITaxonomyContent[] };
  level1Tags?: ITaxonomyContent[];
}

const CheckboxWithTrackEvent = withTrackEvent(
  Checkbox,
) as React.ForwardRefExoticComponent<
  React.ComponentProps<typeof Checkbox> & React.RefAttributes<HTMLInputElement>
>;

const getEventProperties = (tagLabel: string) => ({
  objectType: "Content",
  actionPrefix: "Initiate",
  actionName: "Filter",
  streamProperties: {
    description: `Filter search by Taxonomy tag ${tagLabel}`,
  },
});

// Level 1 Tags
export const CollapsibleTags = ({
  categorizedData,
  level1Tags,
}: CollapsibleTagsProps) => {
  const { isChecked, toggleSelection } = useTaxonomyTagsContext();
  const { blockActionAndShowConfirmationModal, blockFilterChanges } =
    useSearchAdminPermissions();

  const handleChange = (_e: ChangeEvent<HTMLInputElement>, tagId: string) => {
    if (blockFilterChanges) {
      blockActionAndShowConfirmationModal();
      return;
    }

    toggleSelection(tagId, false);
  };

  const level = TaxonomyLevelEnum.COLLAPSE_LEVEL;

  return (
    <>
      {level1Tags?.length &&
        level1Tags.map((tag) => (
          <Collapse
            key={tag.id}
            initialIsHidden={false}
            renderButtonContents={(isHidden, timeout) => (
              <div css={collapseTitleContainer}>
                <CheckboxWithTrackEvent
                  key={tag.id}
                  onChange={(e) => handleChange(e, tag.id)}
                  label={tag.label}
                  checked={isChecked(tag.id)}
                  {...getEventProperties(tag.label)}
                  __cssFor={{
                    labelText: collapseLabel,
                  }}
                  dataProps={{
                    "data-qa-selector":
                      TopicsDataQASelector.SELECT_MAIN_TAXONOMY,
                  }}
                />
                <RotatingChevron
                  isDown={isHidden}
                  timeout={timeout || 0}
                  __cssFor={{ root: collapseButton }}
                />
              </div>
            )}
            __cssFor={collapseStyle}
          >
            <RecursiveTags
              categorizedData={categorizedData}
              parentId={tag.id}
              level={level + 1}
            />
          </Collapse>
        ))}
    </>
  );
};
